import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createPixelTag, updatePixelTag } from '@/api/pixelTagApi';
import { PixelTag } from '@/types/pixel-tag';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface AddEditPixelTagModalProps {
    pixelTag: PixelTag | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: PixelTag) => void;
    onUpdateData: (newData: PixelTag) => void;
}

const AddEditPixelTagModal = ({ pixelTag, isOpen, toggle, onAddData, onUpdateData }: AddEditPixelTagModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const isEditing = !!pixelTag;

    const validationSchema = Yup.object({
        name: Yup.string().trim().required('Nome é obrigatório'),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: pixelTag?.name || '',
        },
        validationSchema,
        onSubmit: (values: any) => {
            setLoading(true);

            if (isEditing && pixelTag) {
                updatePixelTag(pixelTag.id, values)
                    .then((res: any) => {
                        toggle();
                        onUpdateData(res);
                        toast.success('Pixel tag atualizado com sucesso!');
                    })
                    .catch(() => {
                        toast.error('Erro ao atualizar pixel tag');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createPixelTag(values)
                    .then((res: any) => {
                        toggle();
                        onAddData(res);
                        toast.success('Pixel tag criado com sucesso!');
                    })
                    .catch(() => {
                        toast.error('Erro ao criar pixel tag');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            formik.resetForm();
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={toggle}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Editar Pixel Tag' : 'Novo Pixel Tag'}</DialogTitle>
                    <DialogDescription>
                        {isEditing ? 'Edite as informações do pixel tag.' : 'Preencha as informações para criar um novo pixel tag.'}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                }}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Nome</Label>
                            <Input
                                id="name"
                                placeholder="Ex: Conversão Landing Page"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.name && formik.errors.name ? 'border-destructive' : ''}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <p className="text-sm text-destructive">{formik.errors.name}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={toggle}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isEditing ? 'Salvar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditPixelTagModal;
